import { React, Fragment, useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Card,
  Page,
  InlineGrid,
  BlockStack,
  Text,
  Spinner,
  LegacyCard,
  DataTable,
  Badge,
  InlineStack,
  Divider,
  Modal,
  DropZone,
  Checkbox,
  TextField,
  Icon,
  Button,
  Collapsible,
  Link,
  Select,
  Banner,
  Tag,
} from "@shopify/polaris";
import {
  FileIcon,
  CameraIcon,
  PageDownIcon,
  ViewIcon,
  EditIcon,
} from "@shopify/polaris-icons";
import { useNavigate, useParams } from "react-router-dom";
import supabase from "../../supabase-client";
import {
  formatFriendlyDate,
  formatPrice,
  numberWithCommas,
} from "../../utility/funcs";
import StatusBadge from "./StatusBadge";
import FieldCaptureCard from "./FieldCaptureCard";
import SpeiPaymentModal from "./SpeiPaymentModal";
import { speiEnabled, getPayoutReceipt } from "../../utility/pagos";
import { useServiceLive } from "../../utility/useServiceLive";
import { UserAuth } from "../../context/AuthContext";
import { parseCfdiInvoice } from "../../utility/cfdiParser";
import { attachReplacementInvoice } from "../../utility/serviceInvoices";
import PositionDiagram from "./PositionDiagram";
import ExpertReviewCard from "./ExpertReviewCard";

const QUOTE_REASON_LABELS = {
  refacciones: "El taller pide anticipo para refacciones",
  factura_al_final: "El taller factura al terminar el trabajo",
  urgente: "Grúa / traslado urgente",
  otro: "Otro",
};

function ViewService() {
  const { hasPermission, session } = UserAuth();
  const navigate = useNavigate();

  const { serviceId } = useParams();
  const [service, setService] = useState(undefined);
  // Frozen preventive analysis, stored on services.analysis when the service's
  // items were written. Rendered only while the service awaits approval.
  const [analysis, setAnalysis] = useState(undefined);
  const [showComparables, setShowComparables] = useState(false);
  // Categorization check (AI). Separate from `analysis`: it can be re-run on
  // any service, including paid ones, which is how history gets cleaned up.
  const [categorization, setCategorization] = useState(undefined);
  const [checkingCat, setCheckingCat] = useState(false);
  // Service-type catalog, only needed by the inline type editor.
  const [allTypes, setAllTypes] = useState([]);
  const [savingItem, setSavingItem] = useState(null);
  // The type Selects stay hidden until asked for — a read-only table is the
  // normal case, and a row of dropdowns reads like a form you must fill in.
  const [editingTypes, setEditingTypes] = useState(false);
  // Correcting the km before approval (set_service_odometer re-analyses).
  const [odoOpen, setOdoOpen] = useState(false);
  const [odoValue, setOdoValue] = useState("");
  const [odoSaving, setOdoSaving] = useState(false);
  const [odoError, setOdoError] = useState(null);
  const [reviewKey, setReviewKey] = useState(0);
  const canEdit = hasPermission("services.update");
  // Whoever can register a service can attach its CFDI once the provider
  // sends it; closing it as no deducible stays with services.update.
  const canAttachInvoice = canEdit || hasPermission("services.create");
  const [serviceItems, setServiceItems] = useState(undefined);
  const [existingPayments, setExistingPayments] = useState(undefined);
  const [files, setFiles] = useState(undefined);

  // Attaching the CFDI of a quote-first service. The equality check that makes
  // this safe lives in attach_service_invoice, not here — this only reports it.
  const [invoiceXml, setInvoiceXml] = useState(null);
  const [invoicePdf, setInvoicePdf] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState(null);

  // Every CFDI this service has had (service_invoices): a provider may cancel
  // one at SAT and issue a replacement, which is attached here.
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [replXml, setReplXml] = useState(null);
  const [replPdf, setReplPdf] = useState(null);
  const [replSaving, setReplSaving] = useState(false);
  const [replError, setReplError] = useState(null);

  // Closing a quote-first service without a CFDI (provider never invoices).
  const [noDeducibleOpen, setNoDeducibleOpen] = useState(false);
  const [noDeducibleReason, setNoDeducibleReason] = useState("");
  const [noDeducibleSaving, setNoDeducibleSaving] = useState(false);
  const [noDeducibleError, setNoDeducibleError] = useState(null);

  // Deleting a field service the provider never submitted (null = closed,
  // otherwise how many photos the provider already uploaded).
  const [deletePhotos, setDeletePhotos] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [speiModalOpen, setSpeiModalOpen] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(null);

  // A payout can settle after the dialog is closed. When the service status or
  // total_paid changes, pull the payment rows too so the page tells the truth
  // without a reload.
  useServiceLive(serviceId, (row) => {
    setService((prev) => (prev ? { ...prev, ...row } : prev));
    refreshPaymentState();
  });
  const [paymentUploading, setPaymentLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentProof, setPaymentProof] = useState(undefined);
  const [sendProofToProvider, setSendProofToProvider] = useState(true);
  const [loadingPaymentEvidence, setLoadingPaymentEvidence] =
    useState(undefined);

  function getLastServiceInfo(lastServices, currentOdometer, serviceTypeId) {
    // Subleased services have no `car` row (and thus no last_services), so guard
    // against a null/undefined map.
    const entry = lastServices?.[String(serviceTypeId)];

    // If no service recorded:
    if (!entry) {
      return {
        kmsAgo: null,
        timeAgo: null,
        status: "never",
      };
    }

    // Calculate kmsAgo
    const kmsAgo = currentOdometer - entry.odo;

    // Calculate timeAgo
    const serviceDate = new Date(entry.date);
    const now = new Date();

    const diffMs = now - serviceDate;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    let timeAgo = "";
    if (diffSeconds < 60) {
      timeAgo = `hace ${diffSeconds} segundos`;
    } else if (diffMinutes < 60) {
      timeAgo = `hace ${diffMinutes} minutos`;
    } else if (diffHours < 24) {
      timeAgo = `hace ${diffHours} horas`;
    } else {
      timeAgo = `hace ${diffDays} días`;
    }

    return {
      kmsAgo,
      timeAgo,
      status: "ok",
    };
  }

  const currentInvoice = service?.invoice;
  useEffect(() => {
    if (!serviceId) return;
    supabase
      .from("service_invoices")
      .select("id, uuid, folio, total, cancelled_at, replaced_by_id, replaces_id, source, created_at")
      .eq("service_id", serviceId)
      .order("id")
      .then(({ data }) => setInvoiceHistory(data || []));
  }, [serviceId, currentInvoice]);

  // Re-read only what a payment changes (service status/total_paid and the
  // payment rows). Replaces the previous window.location.reload(), which threw
  // away the whole page — and with it any indication of what had just happened
  // to real money.
  const refreshPaymentState = async () => {
    const { data } = await supabase
      .from("services")
      .select(
        "*, car(id, vin, plate, last_services), car_model(name, image_url), car_brand(name), provider(*), sublease_reservation_id, sublease_plate, sublease_vin"
      )
      .eq("id", serviceId)
      .single();
    const { data: paymentData } = await supabase
      .from("service_payments")
      .select("*")
      .eq("service", serviceId);
    if (data) {
      setService(data);
      setPaymentAmount(data.total - data.total_paid);
    }
    setExistingPayments(paymentData || []);
  };

  useEffect(() => {
    const fetchService = async () => {
      const { data, error } = await supabase
        .from("services")
        .select(
          "*, car(id, vin, plate, year, last_services), car_model(name, image_url), car_brand(name), provider(*), sublease_reservation_id, sublease_plate, sublease_vin"
        )
        .eq("id", serviceId)
        .single();
      if (error) {
        console.error(error);
        return;
      }

      const { data: paymentData, error: paymentError } = await supabase
        .from("service_payments")
        .select("*")
        .eq("service", serviceId);

      // The analysis is computed and stored when the service's items are
      // written (flag_service, fired by a trigger on service_items), so it
      // arrives with the service row above — no RPC, and the verdict shown
      // is the one recorded at that moment rather than one recomputed today
      // against a benchmark that has since moved.
      setAnalysis(data?.analysis ?? null);
      setCategorization(data?.categorization ?? null);

      const { data: typeData } = await supabase
        .from("service_types")
        .select("id, name")
        .order("name");
      setAllTypes(typeData || []);

      const { data: fileData } = await supabase.storage
        .from("services-evidence")
        .list(`${serviceId}`, {
          limit: 50,
          sortBy: { column: "name", order: "asc" },
        });

      // A link service's own photos (campo_*) are shown grouped in the
      // provider capture card; listing them here again is just noise.
      setFiles(
        data?.origin === "field"
          ? (fileData || []).filter((f) => !f.name.startsWith("campo_"))
          : fileData,
      );

      setService(data);
      setExistingPayments(paymentData);
      setPaymentAmount(data.total - data.total_paid);
    };

    const fetchServiceItems = async () => {
      const { data, error } = await supabase
        .from("service_items")
        .select("*, type(id, name, interval)")
        .eq("service", serviceId);
      if (error) {
        console.error(error);
        return;
      }
      setServiceItems(data);
    };

    if (serviceId && !service) {
      fetchService();
      fetchServiceItems();
    }
  }, [serviceId]);

  const carDetailComponent = () => {
    if (!service) return;
    // Subleased-car services have no cars row: identity comes from the snapshot
    // columns, and there is no fleet car page to link to.
    const isSub = !service.car;
    const vin = isSub ? service.sublease_vin : service.car.vin;
    const plate = isSub ? service.sublease_plate : service.car.plate;
    return (
      <Card>
        {!isSub && (
          <div style={{ position: "absolute", right: "1rem", top: "1rem" }}>
            <Button
              icon={ViewIcon}
              onClick={() => navigate(`/cars/${service.car.id}`)}
            />
          </div>
        )}

        <InlineGrid columns={2} alignItems="center" gap={300}>
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "right",
            }}
          >
            <img
              alt={service.car_model.name}
              src={service.car_model.image_url}
              style={{
                width: "200px",
                height: "200px",
                alignSelf: "center",
              }}
            />
          </div>
          <BlockStack align="center">
            {isSub && (
              <InlineStack>
                <Badge tone="magic">Subarrendado</Badge>
              </InlineStack>
            )}
            <Text variant="headingMd" tone="subdued">
              {vin}
            </Text>
            <Text variant="headingLg">{plate}</Text>
            <Text variant="headingMd" tone="subdued">
              {service.car_brand.name} | {service.car_model.name}
            </Text>
          </BlockStack>
        </InlineGrid>
      </Card>
    );
  };


  const mechanicDetailComponent = () => {
    if (!service.provider)
      return (
        <Card>
          <Text variant="subdued">Sin proveedor ligado.</Text>
        </Card>
      );
    return (
      <Card>
        <BlockStack gap={200}>
          <div>
            <InlineStack gap={100}>
              <Text variant="headingMd">{service.provider?.name}</Text>
              {service.provider?.is_broker === true ? (
                <Badge tone="magic">Broker</Badge>
              ) : (
                ""
              )}
            </InlineStack>
            <Text>{service.provider?.rfc}</Text>
          </div>
          <Divider />
          <div>
            <Text>{service.provider?.contact_name}</Text>
            <Text>
              {service.provider?.city}, {service.provider?.state}
            </Text>
          </div>
          <Text>{service.provider?.phone}</Text>
          <Divider />
          <div>
            <Text>{service.provider?.banco}</Text>
            <Text>{service.provider?.clabe}</Text>
          </div>
        </BlockStack>
      </Card>
    );
  };

  const itemsTable = () => {
    if (!serviceItems) return;

    const rowsDetail = [];

    // Included rows ($0, service_items.included_in) are work billed inside
    // another concepto: shown under it, never as lines of their own.
    serviceItems.filter((it) => !it.included_in).forEach((item) => {
      const { kmsAgo, timeAgo } = getLastServiceInfo(
        service.car?.last_services,
        service.odometer,
        item.type.id
      );
      const included = serviceItems.filter((x) => x.included_in === item.id);
      const includedIds = included.map((x) => x.type.id);
      return rowsDetail.push([
        <BlockStack gap={100} key={item.id}>
          <Text>{item.description}</Text>
          <PositionDiagram positions={item.positions} />
          {canEdit && editingTypes ? (
            <InlineStack gap={100} blockAlign="center">
              <div style={{ minWidth: "220px" }}>
                <Select
                  label="Tipo"
                  labelHidden
                  disabled={savingItem === item.id}
                  options={allTypes.map((t) => ({
                    label: t.name,
                    value: String(t.id),
                  }))}
                  value={String(item.type.id)}
                  onChange={(value) => changeItemType(item.id, value)}
                />
              </div>
              {savingItem === item.id && <Spinner size="small" />}
              {service.status === "pending_approval" && kmsAgo ? (
                <Text variant="bodySm" tone="subdued">
                  {`Ultimo hace ${numberWithCommas(kmsAgo)}km o ${timeAgo}.`}
                </Text>
              ) : null}
            </InlineStack>
          ) : (
            <Text variant="bodySm">
              {item.type.name}{" "}
              {service.status === "pending_approval" ? (
                kmsAgo ? (
                  ` | Ultimo hace ${numberWithCommas(kmsAgo)}km o ${timeAgo}.`
                ) : (
                  ""
                )
              ) : (
                <></>
              )}
            </Text>
          )}
          {canEdit && editingTypes ? (
            <InlineStack gap={100} blockAlign="center" wrap>
              <Text variant="bodySm" tone="subdued">
                Incluye también:
              </Text>
              {included.map((x) => (
                <Tag
                  key={x.id}
                  disabled={savingItem === item.id}
                  onRemove={() =>
                    changeIncludes(
                      item.id,
                      includedIds.filter((id) => id !== x.type.id)
                    )
                  }
                >
                  {x.type.name}
                </Tag>
              ))}
              <div style={{ minWidth: "180px" }}>
                <Select
                  label="Agregar trabajo incluido"
                  labelHidden
                  disabled={savingItem === item.id}
                  options={[
                    { label: "+ Agregar trabajo", value: "" },
                    ...allTypes
                      .filter(
                        (t) =>
                          t.id !== item.type.id && !includedIds.includes(t.id)
                      )
                      .map((t) => ({ label: t.name, value: String(t.id) })),
                  ]}
                  value=""
                  onChange={(v) =>
                    v && changeIncludes(item.id, [...includedIds, Number(v)])
                  }
                />
              </div>
            </InlineStack>
          ) : (
            included.length > 0 && (
              <Text variant="bodySm" tone="subdued">
                {`Incluye también: ${included
                  .map((x) => x.type.name)
                  .join(", ")}`}
              </Text>
            )
          )}
        </BlockStack>,
        formatPrice(item.unit_price),
        item.quantity,
        formatPrice(item.total),
      ]);
    });

    return (
      <LegacyCard>
        <div style={{ padding: "0.75rem 1rem 0" }}>
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingSm" as="h3">
              Categorización de Conceptos
            </Text>
            <InlineStack gap={200} blockAlign="center">
              {catAgrees && <Badge tone="success">Concuerda</Badge>}
              {canEdit && (
                <Button
                  variant="plain"
                  icon={editingTypes ? undefined : EditIcon}
                  onClick={() => setEditingTypes((v) => !v)}
                  disabled={savingItem !== null}
                >
                  {editingTypes ? "Listo" : "Editar tipos"}
                </Button>
              )}
            </InlineStack>
          </InlineStack>
        </div>
        <DataTable
          showTotalsInFooter
          columnContentTypes={["text", "numeric", "numeric", "numeric"]}
          headings={["Concepto", "Precio Unitario", "Cantidad", "Total"]}
          rows={rowsDetail}
          totals={["", "", "", formatPrice(service.total)]}
        />
      </LegacyCard>
    );
  };

  const getFileIcon = (mimetype) => {
    if (mimetype.includes("image")) return CameraIcon;
    if (mimetype.includes("pdf")) return FileIcon;
    return FileIcon; // default
  };

  const evidenceDetails = () => {
    const openFile = async (filename) => {
      const { data } = await supabase.storage
        .from("services-evidence")
        .createSignedUrl(`${serviceId}/${filename}`, 1200);
      window.open(data.signedUrl, "_blank");
    };

    return (
      <div>
        <Card title="Evidencia de Pago" sectioned>
          <Text variant="headingMd">Evidencia</Text>
          <div
            style={{
              display: "flex",
              overflow: "scroll",
              gap: "1.3rem",
              marginTop: "1rem",
            }}
          >
            {files.length === 0 ? (
              <Text tone="subdued">
                No hay evidencia disponible para este servicio.
              </Text>
            ) : (
              files.map((file) => {
                const icon = getFileIcon(file.metadata.mimetype);
                return (
                  <div
                    key={file.id}
                    onClick={() => openFile(file.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      width: "130px",
                      cursor: "pointer",
                    }}
                  >
                    <BlockStack gap={100} alignment="center">
                      <Icon source={icon} tone="base" />
                      <Text alignment="center" variant="bodySm" truncate>
                        {file.name}
                      </Text>
                      <Text tone="subdued" alignment="center" variant="caption">
                        {(file.metadata.size / 1024).toFixed(1)} KB
                      </Text>
                    </BlockStack>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
    );
  };

  // Attach the CFDI of a service that was quoted, approved and paid without one.
  // The RPC re-checks everything (no invoice yet, UUID unused, issuer RFC is the
  // provider's, total equal to the frozen quote) and refuses otherwise, so a
  // mismatch surfaces here as its message rather than being silently accepted.
  const attachInvoice = async () => {
    setAttaching(true);
    setAttachError(null);
    let cfdi;
    try {
      cfdi = parseCfdiInvoice(await invoiceXml.text());
    } catch (err) {
      setAttaching(false);
      setAttachError("No se pudo leer el XML del CFDI.");
      return;
    }

    const { data, error } = await supabase.rpc("attach_service_invoice", {
      p_service: Number(serviceId),
      p_invoice: cfdi.uuid,
      p_invoice_id: cfdi.folio,
      p_total: cfdi.total,
      p_issuer_rfc: cfdi.emisorRfc,
      p_stamped_at: cfdi.fechaTimbrado,
    });
    if (error) {
      setAttaching(false);
      setAttachError(error.message);
      return;
    }

    // Files go up only once the invoice was accepted — a rejected one is the
    // expected case here, and it should leave nothing behind.
    const invoicePath =
      String(data.performed_at).split("T")[0].replace(/-/g, "/") +
      "/" +
      cfdi.uuid;
    const [{ error: pdfError }, { error: xmlError }] = await Promise.all([
      supabase.storage
        .from("incoming-invoices")
        .upload(invoicePath + ".pdf", invoicePdf),
      supabase.storage
        .from("incoming-invoices")
        .upload(invoicePath + ".xml", invoiceXml),
    ]);
    setAttaching(false);
    if (pdfError || xmlError) {
      console.error(pdfError, xmlError);
      setAttachError(
        "La factura quedó registrada pero los archivos no se pudieron subir. Vuelve a subirlos.",
      );
    }
    setService((prev) => ({ ...prev, ...data }));
    setInvoiceXml(null);
    setInvoicePdf(null);
  };

  const markNoDeducible = async () => {
    setNoDeducibleSaving(true);
    setNoDeducibleError(null);
    const { data, error } = await supabase.rpc("mark_service_non_deductible", {
      p_service: Number(serviceId),
      p_reason: noDeducibleReason,
    });
    setNoDeducibleSaving(false);
    if (error) {
      setNoDeducibleError(error.message);
      return;
    }
    setService((prev) => ({ ...prev, ...data }));
    setNoDeducibleOpen(false);
    setNoDeducibleReason("");
  };

  const openDelete = async () => {
    setDeleteError(null);
    const { data } = await supabase
      .from("service_capture_links")
      .select("photos")
      .eq("service_id", serviceId);
    setDeletePhotos((data || []).reduce((n, l) => n + (l.photos?.length || 0), 0));
  };

  const deleteFieldService = async () => {
    setDeleting(true);
    setDeleteError(null);
    const { data: paths, error } = await supabase.rpc("delete_field_service", {
      p_service: Number(serviceId),
    });
    if (error) {
      setDeleting(false);
      setDeleteError(error.message);
      return;
    }
    // The service is gone either way; a photo left behind is only storage.
    if (paths?.length) {
      const { error: rmErr } = await supabase.storage.from("services-evidence").remove(paths);
      if (rmErr) console.error("No se pudieron borrar las fotos:", rmErr);
    }
    navigate("/services", { replace: true });
  };

  const attachReplacement = async () => {
    setReplSaving(true);
    setReplError(null);
    try {
      const cfdi = parseCfdiInvoice(await replXml.text());
      const { service: updated, filesFailed } = await attachReplacementInvoice({
        serviceId,
        cfdi,
        xmlFile: replXml,
        pdfFile: replPdf,
        replaces: cfdi.substitutes.find(
          (u) => u.toUpperCase() === String(service.invoice).toUpperCase(),
        ) || null,
      });
      setService((prev) => ({ ...prev, ...updated }));
      setReplXml(null);
      setReplPdf(null);
      if (filesFailed)
        setReplError("La factura quedó registrada pero los archivos no se pudieron subir. Vuelve a subirlos.");
    } catch (err) {
      setReplError(err.message || "No se pudo leer el XML del CFDI.");
    } finally {
      setReplSaving(false);
    }
  };

  // Shown only when there is something to say: the current CFDI was cancelled
  // at SAT, or the service has had more than one.
  const cfdiHistory = () => {
    if (!service) return null;
    const cancelled = service.invoice_status === "cancelled";
    if (!cancelled && invoiceHistory.length < 2) return null;
    const byId = Object.fromEntries(invoiceHistory.map((i) => [i.id, i]));
    return (
      <Card>
        <BlockStack gap={300}>
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd">Facturas del servicio</Text>
            {cancelled ? (
              <Badge tone="critical">Factura cancelada</Badge>
            ) : (
              <Badge tone="success">Sustituida</Badge>
            )}
          </InlineStack>

          {cancelled && (
            <Banner tone="critical">
              <p>
                El proveedor canceló ante el SAT la factura {service.invoice_id}
                {service.total_paid > 0
                  ? `, que ya pagamos (${formatPrice(service.total_paid)})`
                  : ""}
                . Pídele la factura sustituta por {formatPrice(service.total)} y
                adjúntala aquí: el pago pasa a la nueva y la cancelada queda en
                el historial.
              </p>
            </Banner>
          )}

          <BlockStack gap={100}>
            {invoiceHistory.map((i) => (
              <InlineStack key={i.id} align="space-between" blockAlign="center">
                <Text variant="bodySm">
                  Factura {i.folio || "—"} · {formatPrice(i.total)}
                  <Text as="span" tone="subdued">
                    {" "}· {String(i.uuid).toUpperCase()}
                  </Text>
                </Text>
                <Text variant="bodySm" tone={i.cancelled_at ? "critical" : "subdued"}>
                  {i.cancelled_at
                    ? `Cancelada el ${formatFriendlyDate(i.cancelled_at)}`
                    : i.replaced_by_id
                      ? "Sustituida"
                      : "Vigente"}
                  {i.replaced_by_id && byId[i.replaced_by_id]
                    ? ` → ${byId[i.replaced_by_id].folio}`
                    : ""}
                </Text>
              </InlineStack>
            ))}
          </BlockStack>

          {cancelled && canAttachInvoice && (
            <BlockStack gap={300}>
              <Divider />
              <DropZone
                accept={"application/pdf, text/xml, application/xml"}
                allowMultiple
                onDrop={(_d, accepted) => {
                  setReplError(null);
                  for (const file of accepted) {
                    const name = file.name.toLowerCase();
                    if (name.endsWith(".xml")) setReplXml(file);
                    if (name.endsWith(".pdf")) setReplPdf(file);
                  }
                }}
              >
                <DropZone.FileUpload
                  actionTitle="Seleccionar Archivos"
                  actionHint="Sube el PDF y XML de la factura sustituta"
                />
              </DropZone>
              {replPdf && (
                <Text variant="bodySm" tone="subdued">PDF: {replPdf.name}</Text>
              )}
              {replXml && (
                <Text variant="bodySm" tone="subdued">XML: {replXml.name}</Text>
              )}
              {replError && (
                <Banner tone="critical" title="No se pudo adjuntar la factura">
                  <p>{replError}</p>
                </Banner>
              )}
              <Button
                variant="primary"
                disabled={!replXml || !replPdf}
                loading={replSaving}
                onClick={attachReplacement}
              >
                Adjuntar factura sustituta
              </Button>
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    );
  };

  // Any anticipo: a quote, or a link service paid from its cotización. A link
  // service still waiting for its CFDI with no quote has nothing to show here.
  const invoiceDetails = () => {
    if (!service || service.quoted_total == null) return null;
    const pending = service.invoice_status === "pending";
    const noDeducible = service.invoice_status === "no_deducible";
    const daysOpen = Math.floor(
      (Date.now() - new Date(service.created_at).getTime()) / 86400000,
    );
    const daysToInvoice =
      service.invoice_uploaded_at &&
      Math.floor(
        (new Date(service.invoice_uploaded_at).getTime() -
          new Date(service.created_at).getTime()) /
          86400000,
      );

    return (
      <Card>
        <BlockStack gap={300}>
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd">Factura</Text>
            {pending ? (
              <Badge tone="critical">Falta factura</Badge>
            ) : noDeducible ? (
              <Badge tone="warning">No deducible</Badge>
            ) : (
              <Badge tone="success">Recibida</Badge>
            )}
          </InlineStack>

          <BlockStack gap={100}>
            <Text tone="subdued">
              Cotizado el {formatFriendlyDate(service.created_at)} por{" "}
              {formatPrice(service.quoted_total)}
            </Text>
            <Text tone="subdued">
              Motivo:{" "}
              {QUOTE_REASON_LABELS[service.quote_reason] ||
                service.quote_reason ||
                "—"}
              {service.quote_reason_note ? ` — ${service.quote_reason_note}` : ""}
            </Text>
            {pending ? (
              <Text tone={daysOpen >= 30 ? "critical" : "caution"}>
                {daysOpen} día(s) sin factura
              </Text>
            ) : noDeducible ? (
              <Text tone="subdued">
                Sin factura, marcado como no deducible el{" "}
                {formatFriendlyDate(service.non_deductible_at)} —{" "}
                {service.non_deductible_reason}
              </Text>
            ) : (
              <Text tone="subdued">
                Factura {service.invoice_id} recibida el{" "}
                {formatFriendlyDate(service.invoice_uploaded_at)}
                {daysToInvoice !== null && daysToInvoice !== undefined
                  ? ` (${daysToInvoice} día(s) después)`
                  : ""}
              </Text>
            )}
          </BlockStack>

          {pending && canAttachInvoice && (
            <BlockStack gap={300}>
              <Divider />
              <Text variant="bodySm" tone="subdued">
                El total del CFDI tiene que ser exactamente{" "}
                {formatPrice(service.quoted_total)} y el emisor{" "}
                {service.provider?.rfc}. Si no coincide, el proveedor debe
                volver a emitirla; el trabajo extra se registra como otro
                servicio.
              </Text>
              <DropZone
                accept={"application/pdf, text/xml, application/xml"}
                allowMultiple
                onDrop={(_d, accepted) => {
                  setAttachError(null);
                  for (const file of accepted) {
                    const name = file.name.toLowerCase();
                    if (name.endsWith(".xml")) setInvoiceXml(file);
                    if (name.endsWith(".pdf")) setInvoicePdf(file);
                  }
                }}
              >
                <DropZone.FileUpload
                  actionTitle="Seleccionar Archivos"
                  actionHint="Sube el PDF y XML de la factura"
                />
              </DropZone>
              {invoicePdf && (
                <Text variant="bodySm" tone="subdued">
                  PDF: {invoicePdf.name}
                </Text>
              )}
              {invoiceXml && (
                <Text variant="bodySm" tone="subdued">
                  XML: {invoiceXml.name}
                </Text>
              )}
              {attachError && (
                <Banner tone="critical" title="No se pudo adjuntar la factura">
                  <p>{attachError}</p>
                </Banner>
              )}
              <Button
                variant="primary"
                disabled={!invoiceXml || !invoicePdf}
                loading={attaching}
                onClick={attachInvoice}
              >
                Adjuntar factura
              </Button>
              {canEdit && (
                <Button
                  variant="plain"
                  tone="critical"
                  onClick={() => {
                    setNoDeducibleError(null);
                    setNoDeducibleOpen(true);
                  }}
                >
                  El proveedor no factura — marcar como no deducible
                </Button>
              )}
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    );
  };

  // Fintoc reports why the beneficiary's bank sent the money back. Only the
  // reasons actually seen are translated; anything else is shown as Fintoc sent
  // it, because inventing a friendly label for an unknown code hides it.
  const returnReasonLabel = (reason) =>
    ({
      exceeds_account_limit: "la cuenta excede su límite de depósitos",
      invalid_account_number: "CLABE inválida",
      non_existing_account: "la cuenta no existe",
      blocked_account: "cuenta bloqueada",
      account_not_found: "no se encontró la cuenta",
      returned: null,
    })[reason] ?? reason ?? null;

  const paymentDetails = () => {
    if (!existingPayments) return;
    const paymentText = () => {
      if (service.status === "paid") {
        return "Este servicio está liquidado.";
      }
      const remaining = service.total - service.total_paid;
      return `Pago pendiente de ${formatPrice(remaining)}`;
    };

    const downloadPaymentEvidence = async (payment) => {
      setLoadingPaymentEvidence(payment.id);
      const { data, error } = await supabase.storage
        .from("services-evidence")
        .createSignedUrl(`payments/${payment.id}.${payment.file_ext}`, 3600);
      setLoadingPaymentEvidence(undefined);
      if (error) {
        console.error(error);
        return;
      }
      window.open(data.signedUrl, "_blank");
    };

    return (
      <div>
        <Card>
          <BlockStack gap={300}>
            <div>
              <Text variant="headingMd">Pago</Text>
              <Text tone="subdued">
                {service?.invoice_status === "pending"
                  ? "Anticipo — sin factura"
                  : service?.invoice_status === "no_deducible"
                    ? "Sin factura — no deducible"
                    : service?.invoice_status === "cancelled"
                      ? `Factura: ${service?.invoice_id || "—"} — cancelada ante el SAT`
                      : `Factura: ${service?.invoice_id || "—"}`}
              </Text>
              <Text tone="subdued">{paymentText()}</Text>
            </div>
            {existingPayments.length > 0 && (
              <BlockStack gap={300}>
                {existingPayments.map((p) => (
                  // key on the fragment: it is the outermost node of the map.
                  <Fragment key={p.id}>
                    <Divider />
                    <InlineStack align="space-between" blockAlign="center">
                      <BlockStack>
                        <InlineStack gap={200} blockAlign="center">
                          {/* A returned payment is struck through, not hidden:
                              the money came back, but the transfer and the
                              comprobante the provider was sent both happened. */}
                          <Text
                            variation="strong"
                            tone={p.reversed_at ? "subdued" : undefined}
                            textDecorationLine={p.reversed_at ? "line-through" : undefined}
                          >
                            ${p.amount.toLocaleString("es-MX")}
                          </Text>
                          {p.reversed_at && <Badge tone="critical">Devuelto</Badge>}
                        </InlineStack>
                        <Text as="span" tone="subdued">
                          {new Date(p.paid_at).toLocaleDateString("es-MX", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {p.method ? ` · ${p.method}` : ""}
                        </Text>
                        {p.reversed_at && (
                          <Text as="span" tone="critical" variant="bodySm">
                            El banco lo devolvió
                            {returnReasonLabel(p.reversed_reason)
                              ? ` — ${returnReasonLabel(p.reversed_reason)}`
                              : ""}
                            {` · ${new Date(p.reversed_at).toLocaleDateString("es-MX", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}`}
                          </Text>
                        )}
                        {!p.file_ext && !p.payout_id && !p.reversed_at && (
                          <InlineStack gap={200} blockAlign="center">
                            <Badge tone="warning">Sin comprobante</Badge>
                          </InlineStack>
                        )}
                      </BlockStack>

                      {/* Uploaded comprobante (manual payments). */}
                      {p.file_ext && (
                        <Button
                          onClick={() => downloadPaymentEvidence(p)}
                          icon={PageDownIcon}
                          variant="plain"
                          loading={loadingPaymentEvidence === p.id}
                        />
                      )}
                      {/* SPEI payouts have no stored file: Fintoc's PDF is
                          fetched on demand because its URL expires in ~1h. */}
                      {!p.file_ext && p.payout_id && (
                        <Button
                          onClick={() => openFintocReceipt(p.payout_id)}
                          icon={PageDownIcon}
                          variant="plain"
                          loading={loadingReceipt === p.payout_id}
                        >
                          Comprobante
                        </Button>
                      )}
                    </InlineStack>
                  </Fragment>
                ))}
              </BlockStack>
            )}
          </BlockStack>
        </Card>
      </div>
    );
  };


  // Price severity comes from the VISIT basket, not from individual lines.
  // Providers do not split invoices the same way — some bill one bundled
  // "Cambio de Aceite" that already includes filters, others itemise them —
  // so a line-level comparison partly measures invoice formatting. On the
  // current queue that cost us 4 false alarms out of 5 (service 1167 read as
  // a 3.97x outlier per line while the whole visit was 0.67x the median).
  // Line-level verdicts stay in services.analysis as supporting detail.
  const PRICE_RANK = { outlier: 3, high: 2, normal: 1, low: 1, unknown: 0 };
  const NEED_RANK = {
    too_soon: 3,   // paid for something the car did not need yet
    overdue: 2,    // an ops miss, not a billing problem
    due: 1,
    no_baseline: 0,
    no_interval: 0,
    unknown: 0,
  };
  const TONE_BY_RANK = { 3: "critical", 2: "warning", 1: "success", 0: undefined };

  const worstBy = (items, field, ranks) =>
    items.reduce(
      (acc, i) => Math.max(acc, ranks[i[field]?.verdict] ?? 0),
      items.length ? 0 : -1
    );

  // Change one line's service type. Narrow on purpose — the RPC can only
  // touch `type`, so a correction here can never desync prices or totals.
  const changeItemType = async (itemId, newTypeId) => {
    setSavingItem(itemId);
    try {
      const { data, error } = await supabase.rpc("set_service_item_type", {
        p_item: itemId,
        p_type: Number(newTypeId),
      });
      if (error) throw new Error(error.message);
      if (data?.changed === false) return;

      // Reflect the change without a reload: the item row, the (possibly
      // re-analysed) snapshot, and the now-resolved categorization finding.
      setServiceItems((prev) =>
        (prev || []).map((it) =>
          it.id === itemId
            ? {
                ...it,
                type: {
                  ...it.type,
                  id: Number(newTypeId),
                  name: data.to,
                },
              }
            : it
        )
      );
      if (data?.analysis !== undefined) setAnalysis(data.analysis);
      setCategorization((prev) =>
        prev
          ? {
              ...prev,
              items: (prev.items || []).map((i) =>
                i.item_id === itemId ? { ...i, agrees: true } : i
              ),
            }
          : prev
      );
    } catch (e) {
      alert("No se pudo cambiar el tipo: " + (e?.message || e));
    } finally {
      setSavingItem(null);
    }
  };

  // Replace the work billed inside a concepto. Like changeItemType it can only
  // touch types, never amounts; the RPC re-analyses a pending service.
  const changeIncludes = async (itemId, typeIds) => {
    setSavingItem(itemId);
    try {
      const { data, error } = await supabase.rpc("set_service_item_includes", {
        p_item: itemId,
        p_types: typeIds,
      });
      if (error) throw new Error(error.message);
      const { data: items, error: itemsError } = await supabase
        .from("service_items")
        .select("*, type(id, name, interval)")
        .eq("service", serviceId);
      if (itemsError) throw new Error(itemsError.message);
      setServiceItems(items);
      if (data?.analysis !== undefined) setAnalysis(data.analysis);
    } catch (e) {
      alert("No se pudo guardar lo incluido: " + (e?.message || e));
    } finally {
      setSavingItem(null);
    }
  };

  const runCategorizationCheck = async () => {
    setCheckingCat(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "check_service_categorization",
        { body: { service_id: Number(serviceId) } }
      );
      if (error) {
        let detail = "";
        try {
          detail = (await error.context?.json())?.error || "";
        } catch {
          /* non-JSON body */
        }
        throw new Error(detail || error.message);
      }
      if (data?.error) throw new Error(data.error);
      const { error: saveError } = await supabase.rpc(
        "save_categorization_check",
        { p_service: Number(serviceId), p_result: data }
      );
      if (saveError) throw new Error(saveError.message);
      setCategorization(data);
    } catch (e) {
      alert("No se pudo revisar la categorización: " + (e?.message || e));
    } finally {
      setCheckingCat(false);
    }
  };

  // When the AI check ran and every line agrees, the result is a single green
  // badge in the Conceptos header — the detail card has nothing to say and is
  // hidden. It only appears when there is something to act on.
  const catMismatches = (categorization?.items || []).filter((i) => !i.agrees);
  const catChecked = !!categorization;
  const catAgrees = catChecked && catMismatches.length === 0;

  const categorizationComponent = () => {
    const mismatches = catMismatches;
    const checked = catChecked;
    if (catAgrees) return null;
    // Nothing to categorize until there are line items (a link service has
    // none until its CFDI is attached).
    if (!serviceItems?.length) return null;
    return (
      <Card>
        <BlockStack gap={200}>
          <InlineStack gap={200} blockAlign="center">
            <Text variant="headingSm" as="h3">
              Categorización
            </Text>
            {checked && (
              <Badge
                tone={
                  mismatches.some((m) => m.confidence === "high")
                    ? "warning"
                    : mismatches.length
                    ? undefined
                    : "success"
                }
              >
                {mismatches.length
                  ? `${mismatches.length} por revisar`
                  : "Concuerda"}
              </Badge>
            )}
            <Button
              variant="plain"
              loading={checkingCat}
              onClick={runCategorizationCheck}
            >
              {checked ? "Volver a revisar" : "Revisar con IA"}
            </Button>
          </InlineStack>
          {mismatches.length > 0 && (
            <BlockStack gap={100}>
              {mismatches.map((m) => (
                <InlineStack key={m.item_id} gap={200} blockAlign="center">
                  <Text variant="bodySm">
                    <b>{m.current_type_name}</b> → parece{" "}
                    <b>{m.suggested_type_name}</b> ({m.confidence}). {m.reason}
                  </Text>
                  {canEdit && m.suggested_type_id && (
                    <Button
                      variant="plain"
                      loading={savingItem === m.item_id}
                      onClick={() =>
                        changeItemType(m.item_id, m.suggested_type_id)
                      }
                    >
                      Aplicar
                    </Button>
                  )}
                </InlineStack>
              ))}
            </BlockStack>
          )}
          {checked && mismatches.length === 0 && (
            <Text variant="bodySm" tone="subdued">
              Cada renglón concuerda con su tipo de servicio.
            </Text>
          )}
          {!checked && (
            <Text variant="bodySm" tone="subdued">
              Compara la descripción de cada renglón contra el tipo con el que se
              capturó. Un renglón mal capturado ensucia los comparativos de
              precio.
            </Text>
          )}
        </BlockStack>
      </Card>
    );
  };

  const analysisComponent = () => {
    if (service?.status !== "pending_approval") return null;
    const items = analysis?.items || [];
    if (items.length === 0) return null;

    const basket = analysis?.basket;
    const basketUsable = ["model", "category", "global", "composite"].includes(basket?.basis);
    // Composite = not enough same-model visits with exactly these concepts, so
    // the expected price is the sum of each concept's own median (same model
    // when it has data). It has no comparable visits to list.
    const composite = basket?.basis === "composite";
    // The comparable visits behind the median — so "¿dónde lo conseguimos por
    // ese precio?" has a concrete answer instead of a number to take on faith.
    const examples = basket?.examples || [];
    // A preventive concepto that also bills repairs (service 1250: birlo,
    // foco) is dearer than its comparables for a reason the benchmark cannot
    // see — worth a look, not an alarm. Same cap as flag_service.
    const mixedBundle = !!basket?.mixed_bundle;
    const priceRank = basketUsable
      ? Math.min(PRICE_RANK[basket.verdict] ?? 0, mixedBundle ? 2 : 3)
      : 0;
    // Tire-only visits are benchmarked per piece (line ÷ wheels marked), so
    // every amount in this card is per tire, not per visit.
    const perPiece = !!basket?.per_piece;
    const needRank = worstBy(items, "necessity", NEED_RANK);

    const problems = [];
    if (basketUsable && ["outlier", "high"].includes(basket.verdict))
      problems.push(
        `${
          perPiece
            ? `${basket.anchor_name} cuesta $${numberWithCommas(
                Math.round(basket.total)
              )} por pieza`
            : `la visita de ${basket.anchor_name} cuesta $${numberWithCommas(
                Math.round(basket.total)
              )}`
        } contra ${composite ? "un esperado de" : "una mediana de"} $${numberWithCommas(
          Math.round(basket.median)
        )} (${basket.ratio}x, n=${basket.n})${
          mixedBundle
            ? `, aunque el mismo concepto incluye ${(basket.bundle_extras || [])
                .join(", ")
                .toLowerCase()}, así que el comparativo es aproximado`
            : ""
        }`
      );

    // Every verdict that colours a badge has to appear here too, or the
    // card contradicts itself (a yellow Intervalo above "Todo correcto").
    items.forEach((i) => {
      if (i.necessity?.verdict === "too_soon")
        problems.push(
          `${i.type_name} se hizo antes de tiempo, a ${numberWithCommas(
            i.necessity.km_since
          )} km de ${numberWithCommas(i.necessity.km_interval)}`
        );
      if (i.necessity?.verdict === "overdue")
        problems.push(
          `${i.type_name} se hizo tarde, a ${numberWithCommas(
            i.necessity.km_since
          )} km de ${numberWithCommas(i.necessity.km_interval)}`
        );
    });

    const summary =
      problems.length === 0
        ? "Todo correcto: precios e intervalos dentro de lo normal."
        : `Revisar: ${problems.join("; ")}.`;

    // Badges are only meaningful when a benchmark or interval existed. Say so
    // rather than showing a green badge that means "we could not check".
    const priceLabel = basketUsable
      ? `${perPiece ? "Precio por pieza" : "Precio"} (${numberWithCommas(Math.round(basket.total))} vs ${numberWithCommas(
          Math.round(basket.median)
        )})`
      : "Precio sin comparativo";
    const needLabel =
      needRank === 0 ? "Intervalo sin referencia" : "Intervalo";

    return (
      <Card>
        <BlockStack gap={200}>
          <InlineStack gap={200} blockAlign="center">
            <Text variant="headingSm" as="h3">
              Preventivos
            </Text>
            <Badge tone={TONE_BY_RANK[priceRank]}>{priceLabel}</Badge>
            <Badge tone={TONE_BY_RANK[needRank]}>{needLabel}</Badge>
          </InlineStack>
          <Text variant="bodySm" tone={problems.length ? undefined : "subdued"}>
            {summary}
          </Text>
          {examples.length > 0 && (
            <BlockStack gap={100}>
              <InlineStack>
                <Button
                  variant="plain"
                  disclosure={showComparables ? "up" : "down"}
                  onClick={() => setShowComparables((v) => !v)}
                >
                  {showComparables
                    ? "Ocultar comparables"
                    : `Ver ${examples.length} visitas comparables`}
                </Button>
              </InlineStack>
              <Collapsible
                open={showComparables}
                id="comparables-preventivos"
                transition={false}
              >
                <DataTable
                  columnContentTypes={[
                    "text",
                    "text",
                    "text",
                    "text",
                    "text",
                    "numeric",
                  ]}
                  headings={[
                    "Servicio",
                    "Fecha",
                    "Proveedor",
                    "Placa",
                    "Modelo",
                    perPiece ? "Por pieza (sin IVA)" : "Total (sin IVA)",
                  ]}
                  rows={examples.map((e) => [
                    // New tab on purpose: the reviewer is mid-approval on this
                    // service and should not lose it to go read a comparable.
                    <Link
                      key={e.service_id}
                      url={`/services/${e.service_id}`}
                      target="_blank"
                    >
                      {`#${e.service_id}`}
                    </Link>,
                    formatFriendlyDate(e.date),
                    e.provider || "—",
                    e.plate || "—",
                    e.model || "—",
                    formatPrice(e.total),
                  ])}
                />
                <Text variant="bodySm" tone="subdued">
                  Visitas con los mismos conceptos que este servicio en los
                  últimos {analysis.window_months} meses, sumando solo esos
                  conceptos, de menor a mayor. Verifica el modelo: cuando la
                  comparación es por categoría pueden ser unidades distintas.
                </Text>
              </Collapsible>
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    );
  };


  const pageContent = () => {
    // A link service before its CFDI: no items, total or payments yet.
    const awaitingCfdi =
      service.origin === "field" &&
      ["awaiting_provider", "awaiting_invoice"].includes(service.status);
    return (
      <BlockStack gap={300}>
        <InlineGrid columns={["twoThirds", "oneThird"]} gap={300}>
          {carDetailComponent()}
          {mechanicDetailComponent()}
        </InlineGrid>
        {service.origin === "field" && (
          <FieldCaptureCard
            service={service}
            canEdit={hasPermission("services.create")}
          />
        )}
        {invoiceDetails()}
        {cfdiHistory()}
        {analysisComponent()}
        {service.status === "pending_approval" && !awaitingCfdi && (
          <ExpertReviewCard
            key={`${service.id}-${reviewKey}`}
            serviceId={service.id}
            initial={service.ai_review}
            reviewedAt={service.ai_reviewed_at}
          />
        )}
        {!awaitingCfdi && categorizationComponent()}
        {!awaitingCfdi && itemsTable()}
        {!awaitingCfdi && (
          <InlineGrid columns={["twoThirds", "oneThird"]} gap={300}>
            {evidenceDetails()}
            {paymentDetails()}
          </InlineGrid>
        )}
      </BlockStack>
    );
  };

  const actionGroup = () => {
    const group = [];
    if (!service) return;
    const handleUpdate = async (status) => {
      const { data, error } = await supabase
        .from("services")
        .update({ status: status })
        .eq("id", serviceId)
        .single();
      if (error) return;
      setService({ ...service, status: status });
    };
    const downloadInvoice = async (type) => {
      // Usually <performed_at>/<uuid>, but a replacement CFDI can live under
      // the date of the service it was first uploaded to — ask where it is.
      const { data: found } = await supabase.rpc("service_invoice_file_path", {
        p_uuid: service.invoice,
        p_ext: type,
      });
      const invoicePath =
        found ||
        service.performed_at.split("T")[0].replace(/-/g, "/") +
          "/" +
          service.invoice +
          `.${type}`;
      const { data } = await supabase.storage
        .from("incoming-invoices")
        .createSignedUrl(invoicePath, 1200, { download: true });
      window.open(data.signedUrl, "_blank");
    };
    if (
      ["pending_approval"].includes(service.status) &&
      hasPermission("services.update")
    ) {
      group.push({
        title: "Status",
        onClick: (openActions) => {
          openActions();
        },
        actions: [
          {
            // A service can arrive already paid (paid on a card at the counter
            // and recorded at upload). Approving it then has nothing left to
            // authorise, so it goes straight to "paid" rather than sitting in
            // payment_pending with a zero balance.
            content: "Aprobar",
            onAction: () =>
              handleUpdate(
                Number(service.total_paid || 0) >= Number(service.total)
                  ? "paid"
                  : "payment_pending"
              ),
          },
          { content: "Negar", onAction: () => handleUpdate("denied") },
        ],
      });
    }
    if (service.invoice)
      group.push({
        title: "Descargar Factura",
        onClick: (openActions) => {
          openActions();
        },
        actions: [
          {
            content: "Descargar PDF",
            onAction: () => downloadInvoice("pdf"),
          },
          { content: "Descargar XML", onAction: () => downloadInvoice("xml") },
        ],
      });

    return group;
  };

  const openFintocReceipt = async (payoutId) => {
    setLoadingReceipt(payoutId);
    try {
      const { download_url } = await getPayoutReceipt(payoutId);
      window.open(download_url, "_blank", "noopener");
    } catch (err) {
      console.error("No se pudo obtener el comprobante:", err);
    } finally {
      setLoadingReceipt(null);
    }
  };

  const primaryActionDetail = () => {
    // Field service captured by the provider: staff attach the CFDI (or, if
    // the provider wants to be paid first, the cotización) through the regular
    // NewService flow, which updates this row instead of inserting.
    if (service?.status === "awaiting_invoice") {
      if (!hasPermission("services.create")) return null;
      return {
        content: "Completar con factura o cotización",
        url: `/services/new?fieldService=${service.id}`,
      };
    }
    if (!["payment_pending", "partially_paid"].includes(service?.status))
      return null;
    // SPEI is the only way to pay a service. The old manual-comprobante modal
    // is kept only as a fallback for when the payments service is not
    // configured (e.g. a dev environment without REACT_APP_PAGOS_URL), so the
    // page is never left with a dead Pagar button.
    if (speiEnabled()) {
      if (!hasPermission("services.pay")) return null;
      return { content: "Pagar", onAction: () => setSpeiModalOpen(true) };
    }
    return { content: "Pagar", onAction: () => setPaymentModalOpen(true) };
  };

  const downloadReport = async () => {
    const c = service.car || { vin: service.sublease_vin, plate: service.sublease_plate };
    const cm = service.car_model;
    const cb = service.car_brand;
    const serv = serviceItems.map((i) => ({ name: i.type.name }));
    const next_serv = serviceItems
      .filter((i) => i.type.interval != null) // keep only ones with interval
      .map((i) => ({
        name: i.type.name,
        odometer: i.type.interval + service.odometer,
      }));

    const data = {
      template: "service_detail",
      output: "file",
      vars: {
        service_id: service.id,
        car: {
          vin: c.vin,
          plate: c.plate,
          model: cm.name,
          brand: cb.name,
          odometer: service.odometer,
        },
        service: { date: service.performed_at },
        line_items: serv,
        next_line_items: next_serv,
      },
    };
    if (files.length > 0) {
      const allowed = ["png", "jpg", "jpeg"];

      // Step 1 and 2: filter + limit to 3
      const top3 = files
        .filter((x) => {
          const ext = x.name.toLowerCase().split(".").pop();
          return allowed.includes(ext);
        })
        .slice(0, 3);

      // Step 3: create signed URLs for each file
      const signed = await Promise.all(
        top3.map(async (file) => {
          const { data: signedUrlData, error } = await supabase.storage
            .from("services-evidence")
            .createSignedUrl(`${serviceId}/${file.name}`, 60 * 5); // 5 minutes

          if (error) {
            console.error("Error signing URL:", error);
            return null;
          }

          return { url: signedUrlData.signedUrl };
        })
      );

      // Step 4: filter out any nulls (just in case)
      const result = signed.filter(Boolean);
      data["vars"]["proof_images"] = result;
    }

    const response = await fetch("https://api.gomove.mx/make_pdf/generate", {
      method: "POST", // or GET
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error("Failed:", response.status);
      return;
    }

    // Convert to blob
    const blob = await response.blob();

    // Create a temporary URL and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Reporte de Servicio ${serviceId}.pdf`; // name of the file
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
  };

  const releaseCarFromService = async () => {
    // Close the service block by setting end date to now
    const { data: blocks } = await supabase
      .from("reservations")
      .select("id, date_range")
      .eq("service_id", serviceId)
      .eq("type", "service")
      .eq("status", "active");

    if (blocks && blocks.length > 0) {
      const now = new Date().toISOString();
      for (const block of blocks) {
        // Parse the start date from the range and set end to now
        const startDate = block.date_range?.replace(/[\[\]()]/g, "").split(",")[0]?.replace(/"/g, "").trim();
        await supabase
          .from("reservations")
          .update({
            date_range: `[${startDate},${now}]`,
            status: "completed",
          })
          .eq("id", block.id);
      }
      alert("Auto liberado");
    } else {
      alert("No se encontró bloqueo de servicio activo");
    }
  };

  const saveOdometer = async () => {
    setOdoSaving(true);
    setOdoError(null);
    const { data, error } = await supabase.rpc("set_service_odometer", {
      p_service: Number(serviceId),
      p_odometer: Math.round(Number(odoValue)),
    });
    if (error) {
      setOdoSaving(false);
      setOdoError(error.message);
      return;
    }
    setService((prev) => ({ ...prev, odometer: data.to, ai_review: { status: "running" } }));
    setAnalysis(data.analysis ?? null);
    // The AI opinion was about the old km: start a fresh one (the card polls).
    supabase.functions.invoke("service_expert_review", { body: { service_id: Number(serviceId) } });
    setReviewKey((k) => k + 1);
    setOdoSaving(false);
    setOdoOpen(false);
  };

  const secondaryActions = () => {
    const group = [];
    if (service?.status === "pending_approval" && canEdit)
      group.push({
        content: "Corregir odómetro",
        onAction: () => {
          setOdoValue(service.odometer != null ? String(service.odometer) : "");
          setOdoError(null);
          setOdoOpen(true);
        },
      });
    if (service?.origin === "field" && service?.status === "awaiting_provider" && canEdit)
      group.push({ content: "Eliminar servicio", destructive: true, onAction: openDelete });
    group.push({
      content: "Liberar Auto",
      onAction: releaseCarFromService,
    });
    if (["paid", "partially_paid", "payment_pending"].includes(service?.status))
      group.push({
        content: "Bitácora",
        onAction: () => downloadReport(),
      });
    return group;
  };

  const savePayment = async () => {
    setPaymentLoading(true);
    const file_ext = paymentProof.name.split(".").pop();
    const paymentObject = {
      service: serviceId,
      amount: paymentAmount,
      file_ext,
      notify: sendProofToProvider,
    };
    const { data, error } = await supabase
      .from("service_payments")
      .insert(paymentObject)
      .select("id")
      .single();
    if (error) {
      console.error(error);
      return;
    }

    const { error: fileError } = await supabase.storage
      .from("services-evidence")
      .upload(`payments/${data.id}.${file_ext}`, paymentProof);
    if (fileError) {
      console.error(fileError);
      await supabase
        .from("service_payments")
        .delete()
        .eq("id", data.id)
        .single();
      return;
    }

    window.location.reload();
  };

  return (
    <AppWrapper>
      <Page
        title={`Servicio #${serviceId}`}
        titleMetadata={
          <InlineStack gap="200">
            <StatusBadge status={service?.status} />
            {service?.invoice_status === "pending" &&
              service?.quoted_total != null && (
                <Badge tone="critical">Falta factura</Badge>
              )}
            {service?.invoice_status === "no_deducible" && (
              <Badge tone="warning">No deducible</Badge>
            )}
            {service?.invoice_status === "cancelled" && (
              <Badge tone="critical">Factura cancelada</Badge>
            )}
          </InlineStack>
        }
        subtitle={
          service &&
          service.status !== "awaiting_provider" &&
          `${formatFriendlyDate(service.performed_at)}${
            service.odometer != null ? ` a los ${numberWithCommas(service.odometer)}km` : ""
          }`
        }
        backAction={{ onAction: () => navigate(-1) }}
        actionGroups={actionGroup()}
        primaryAction={primaryActionDetail()}
        secondaryActions={secondaryActions()}
      >
        {service === undefined || serviceItems === undefined ? (
          <Spinner size="small" />
        ) : (
          pageContent()
        )}
      </Page>
      <Modal
        open={odoOpen}
        onClose={() => setOdoOpen(false)}
        title="Corregir odómetro"
        primaryAction={{
          content: "Guardar",
          loading: odoSaving,
          disabled: odoValue === "" || Number(odoValue) < 0,
          onAction: saveOdometer,
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setOdoOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <TextField
              label="Km al momento del servicio"
              type="number"
              value={odoValue}
              onChange={setOdoValue}
              autoComplete="off"
              suffix="km"
              error={odoError || undefined}
            />
            <Text tone="subdued" variant="bodySm">
              Se vuelven a revisar los intervalos y la opinión experta con el nuevo km.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        size="small"
        open={noDeducibleOpen}
        onClose={() => setNoDeducibleOpen(false)}
        title="Cerrar sin factura (no deducible)"
        primaryAction={{
          content: "Marcar como no deducible",
          destructive: true,
          loading: noDeducibleSaving,
          disabled: noDeducibleReason.trim().length < 5,
          onAction: markNoDeducible,
        }}
        secondaryActions={[
          { content: "Cancelar", onAction: () => setNoDeducibleOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap={300}>
            <Text tone="subdued">
              El servicio deja de esperar factura y queda como gasto no
              deducible. Ya no se le pedirá el CFDI al proveedor. No se puede
              deshacer.
            </Text>
            <TextField
              label="¿Por qué no habrá factura?"
              autoComplete="off"
              value={noDeducibleReason}
              onChange={setNoDeducibleReason}
              placeholder="Ej. El proveedor no emite facturas"
            />
            {noDeducibleError && (
              <Banner tone="critical">
                <p>{noDeducibleError}</p>
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        size="small"
        open={deletePhotos !== null}
        onClose={() => setDeletePhotos(null)}
        title={`Eliminar servicio #${serviceId}`}
        primaryAction={{
          content: "Eliminar",
          destructive: true,
          loading: deleting,
          onAction: deleteFieldService,
        }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setDeletePhotos(null) }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text>
              {deletePhotos > 0
                ? `El proveedor ya subió ${deletePhotos} ${deletePhotos === 1 ? "foto" : "fotos"}; se borrarán. La liga deja de funcionar.`
                : "La liga del proveedor deja de funcionar."}
            </Text>
            {deleteError && (
              <Banner tone="critical">
                <p>{deleteError}</p>
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
      <SpeiPaymentModal
        open={speiModalOpen}
        payableKind="service"
        payableId={serviceId}
        onClose={() => setSpeiModalOpen(false)}
        onAuthorized={() => refreshPaymentState()}
      />
      <Modal
        size="small"
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentProof(undefined);
        }}
        title="Pagar Servicio"
        primaryAction={{
          content: "Marcar como Pagado",
          onAction: savePayment,
          disabled: paymentProof === undefined,
          loading: paymentUploading,
        }}
      >
        <Modal.Section>
          <BlockStack gap={300}>
            <TextField
              prefix="$"
              value={paymentAmount}
              type="number"
              onChange={(value) => setPaymentAmount(value)}
              min={0}
            />
            <DropZone
              type="file"
              onDrop={(file) => {
                setPaymentProof(file[0]);
              }}
            >
              <DropZone.FileUpload
                actionTitle={
                  paymentProof === undefined
                    ? "Cargar Comprobante"
                    : "Comprobante Listo"
                }
                actionHint={paymentProof === undefined || paymentProof?.name}
              />
            </DropZone>
            <Checkbox
              label="Enviar comprobante a proveedor."
              checked={sendProofToProvider}
              onChange={(value) => setSendProofToProvider(value)}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </AppWrapper>
  );
}

export default ViewService;
